import { supabase } from './supabase';
import { v4 as uuidv4 } from 'uuid';
import { convertToWebP } from './image-conversion';

// Download an image from a URL and convert it to a File object
export const downloadImageAsFile = async (imageUrl: string, fileName?: string): Promise<File | null> => {
  try {
    // Clean up the URL if it contains invalid encoded parts or API keys
    const cleanedUrl = cleanupGoogleUrl(imageUrl);
    
    // For Google Photos URLs, we need special handling to avoid CORS issues
    if (cleanedUrl.includes('googleusercontent.com') || cleanedUrl.includes('gstatic.com') || 
        cleanedUrl.includes('maps.googleapis.com') || cleanedUrl.includes('maps.gstatic.com')) {
      console.log('Processing Google image');
      
      try {
        // For Google images, use a more robust approach with multiple fallbacks
        return await downloadExternalImageWithFallbacks(cleanedUrl, fileName);
      } catch (err) {
        console.warn('Error with Google image:', err);
        return await createPlaceholderImage();
      }
    }
    
    // Regular image fetch for non-Google images with fallbacks
    try {
      return await downloadExternalImageWithFallbacks(cleanedUrl, fileName);
    } catch (error) {
      console.error('Error downloading image:', error);
      return await createPlaceholderImage();
    }
  } catch (error) {
    console.error('Error in downloadImageAsFile:', error);
    return await createPlaceholderImage();
  }
};

// Clean up problematic Google URLs
const cleanupGoogleUrl = (url: string): string => {
  try {
    // Remove any API keys or tokens from the URL
    if (url.includes('key=') || url.includes('token=')) {
      const urlObj = new URL(url);
      
      // Remove Google API key and token parameters
      urlObj.searchParams.delete('key');
      urlObj.searchParams.delete('token');
      
      // Clean up any double-encoded components
      let cleanedPath = urlObj.pathname;
      if (cleanedPath.includes('%252F')) {
        cleanedPath = cleanedPath.replace(/%252F/g, '%2F');
      }
      urlObj.pathname = cleanedPath;
      
      return urlObj.toString();
    }
    
    // Fix incorrectly formatted storage URLs
    if (url.includes('supabase.co/storage') && url.includes('%252F')) {
      // Handle malformed Supabase storage URLs
      return url.replace(/%252F/g, '%2F');
    }
    
    return url;
  } catch (error) {
    console.warn('Error cleaning URL, using original:', error);
    return url;
  }
};

// Download an external image with CORS handling and multiple fallback strategies
const downloadExternalImageWithFallbacks = async (imageUrl: string, fileName?: string): Promise<File | null> => {
  const fallbackStrategies = [
    fetchWithDirectRequest,
    fetchWithCredentials,
    fetchWithNoCache,
    fetchWithProxyIfNeeded,
    fetchWithNoCorsModeIfPossible
  ];
  
  // Try each strategy until one works
  for (const strategy of fallbackStrategies) {
    try {
      const file = await strategy(imageUrl, fileName);
      if (file) return file;
    } catch (error) {
      console.warn(`Strategy failed for ${imageUrl}:`, error);
      // Continue to next strategy
    }
  }
  
  // If all strategies fail, create placeholder
  console.error('All download strategies failed for:', imageUrl);
  return await createPlaceholderImage();
};

// Strategy 1: Direct fetch request
const fetchWithDirectRequest = async (imageUrl: string, fileName?: string): Promise<File | null> => {
  console.log('Trying direct fetch for:', imageUrl);
  const response = await fetch(imageUrl);
  
  if (!response.ok) {
    throw new Error(`Failed with status: ${response.status}`);
  }
  
  return createFileFromResponse(response, imageUrl, fileName);
};

// Strategy 2: Fetch with credentials
const fetchWithCredentials = async (imageUrl: string, fileName?: string): Promise<File | null> => {
  console.log('Trying fetch with credentials for:', imageUrl);
  const response = await fetch(imageUrl, {
    credentials: 'omit',
    mode: 'cors'
  });
  
  if (!response.ok) {
    throw new Error(`Failed with status: ${response.status}`);
  }
  
  return createFileFromResponse(response, imageUrl, fileName);
};

// Strategy 3: Fetch with no-cache
const fetchWithNoCache = async (imageUrl: string, fileName?: string): Promise<File | null> => {
  console.log('Trying fetch with no-cache for:', imageUrl);
  const response = await fetch(imageUrl, {
    cache: 'no-cache',
    mode: 'cors',
    headers: {
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache'
    }
  });
  
  if (!response.ok) {
    throw new Error(`Failed with status: ${response.status}`);
  }
  
  return createFileFromResponse(response, imageUrl, fileName);
};

// Strategy 4: Use proxy approach for certain images
const fetchWithProxyIfNeeded = async (imageUrl: string, fileName?: string): Promise<File | null> => {
  if (imageUrl.includes('googleapis.com') || imageUrl.includes('gstatic.com')) {
    console.log('Trying fetch with proxy approach for:', imageUrl);
    
    // Use an image proxy service or base64 placeholder for Google images
    // This is a simplified proxy approach - in production, use a proper image proxy
    try {
      // Create a placeholder with image info
      const placeholderSvg = createInfoPlaceholder(imageUrl);
      const blob = new Blob([placeholderSvg], { type: 'image/svg+xml' });
      const fileExt = imageUrl.split('.').pop()?.split('?')[0] || 'svg';
      const placeholderFileName = fileName || `placeholder_${uuidv4()}.${fileExt}`;
      
      return new File([blob], placeholderFileName, { type: 'image/svg+xml' });
    } catch (e) {
      throw new Error('Proxy approach failed: ' + e);
    }
  }
  
  throw new Error('Not a Google image, skipping proxy approach');
};

// Strategy 5: Fetch with no-cors mode if possible (limited usage)
const fetchWithNoCorsModeIfPossible = async (imageUrl: string, fileName?: string): Promise<File | null> => {
  console.log('Trying fetch with no-cors for:', imageUrl);
  
  try {
    // First try with regular cors mode
    const response = await fetch(imageUrl, { mode: 'cors' });
    if (response.ok) {
      return createFileFromResponse(response, imageUrl, fileName);
    }
  } catch (e) {
    console.warn('Regular cors mode failed, trying no-cors');
  }
  
  // If we got here, try no-cors mode - but this has limitations
  // This may create an opaque response that can't be read directly
  try {
    const response = await fetch(imageUrl, { mode: 'no-cors' });
    // We can't check response.ok on opaque responses
    return createFileFromResponse(response, imageUrl, fileName);
  } catch (e) {
    console.error('No-cors mode also failed');
    throw e;
  }
};

// Helper to create a File from a response
const createFileFromResponse = async (response: Response, imageUrl: string, fileName?: string): Promise<File> => {
  // Convert to blob
  const blob = await response.blob();
  
  // Create a File from the blob
  const fileExtension = 'webp'; // Always use WebP for better compression
  const generatedFileName = fileName || `place_image_${uuidv4()}.${fileExtension}`;
  
  // Create a File with WebP mime type
  const file = new File([blob], generatedFileName, { type: blob.type || 'image/jpeg' });
  
  // Convert to WebP
  return await convertToWebP(file);
};

// Create a placeholder image with information
const createInfoPlaceholder = (imageUrl: string): string => {
  // Create a descriptive SVG that shows the image URL that failed
  const displayUrl = imageUrl.substring(0, 30) + '...';
  return `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="150" viewBox="0 0 200 150">
    <rect width="200" height="150" fill="#f0f0f0"/>
    <text x="50%" y="50%" font-family="Arial" font-size="12" fill="#999" text-anchor="middle">Image unavailable</text>
    <text x="50%" y="70%" font-family="Arial" font-size="8" fill="#888" text-anchor="middle">${displayUrl}</text>
  </svg>`;
};

// Create a placeholder image when fetching fails - using inline SVG data to avoid fetch
const createPlaceholderImage = async (): Promise<File | null> => {
  try {
    // Create a simple SVG placeholder without using fetch
    const svgContent = `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="150" viewBox="0 0 200 150">
        <rect width="200" height="150" fill="#f0f0f0"/>
        <text x="50%" y="50%" font-family="Arial" font-size="12" fill="#999" text-anchor="middle">Image unavailable</text>
      </svg>`;
    
    // Convert SVG string to Blob without using fetch
    const blob = new Blob([svgContent], { type: 'image/svg+xml' });
    return new File([blob], `placeholder_${uuidv4()}.svg`, { type: 'image/svg+xml' });
  } catch (error) {
    console.error('Error creating placeholder image:', error);
    
    // Final fallback - create a transparent pixel without fetch
    try {
      // Base64 encoded 1x1 transparent pixel PNG
      const base64 = 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
      
      // Decode base64 to binary
      const binaryString = window.atob(base64);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      const blob = new Blob([bytes], { type: 'image/png' });
      return new File([blob], `empty_placeholder_${uuidv4()}.png`, { type: 'image/png' });
    } catch (e) {
      console.error('Failed to create even a fallback image:', e);
      return null;
    }
  }
};

// Compress an image file before uploading
export const compressImage = async (file: File): Promise<File> => {
  try {
    // First convert to WebP for better compression
    const webpFile = await convertToWebP(file);
    
    // Check if the file is already small enough after WebP conversion
    if (webpFile.size < 2 * 1024 * 1024) {
      return webpFile;
    }
    
    // If still too large, apply additional compression
    const img = document.createElement('img');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Create an image from the file
    const imgSrc = URL.createObjectURL(webpFile);
    
    // Return a promise that resolves with the compressed image
    return new Promise((resolve, reject) => {
      img.onload = () => {
        URL.revokeObjectURL(imgSrc);
        
        // Calculate new dimensions (max 1200px width or height)
        const MAX_WIDTH = 1200;
        const MAX_HEIGHT = 1200;
        let width = img.width;
        let height = img.height;
        
        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width;
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height;
            height = MAX_HEIGHT;
          }
        }
        
        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;
        
        // Draw image on canvas
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        ctx.drawImage(img, 0, 0, width, height);
        
        // Get compressed image as blob with WebP format
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Failed to compress image'));
            return;
          }
          
          const compressedFile = new File(
            [blob], 
            webpFile.name, 
            { type: 'image/webp', lastModified: Date.now() }
          );
          
          console.log(`Compressed image from ${webpFile.size} to ${compressedFile.size} bytes`);
          resolve(compressedFile);
        }, 'image/webp', 0.8);
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(imgSrc);
        reject(new Error('Failed to load image for compression'));
      };
      
      img.src = imgSrc;
    });
  } catch (error) {
    console.error('Error compressing image:', error);
    return file; // Return original file if compression fails
  }
};

// Upload an image to Supabase storage
export const uploadImageToStorage = async (
  file: File,
  placeName: string,
  placeId?: string
): Promise<string | null> => {
  try {
    // Create a sanitized folder/file name based on the place name
    const sanitizedPlaceName = placeName
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '_')
      .replace(/_+/g, '_');
    
    // Generate a unique path for the image with WebP extension
    const uniqueId = placeId || uuidv4().substring(0, 8);
    const filePath = `${sanitizedPlaceName}/${uniqueId}_${Date.now()}.webp`;
    
    // Convert to WebP and compress the image before uploading
    const webpFile = await convertToWebP(file);
    const compressedFile = await compressImage(webpFile);
    
    // Upload the file
    const { data, error } = await supabase.storage
      .from('place_images')
      .upload(filePath, compressedFile, {
        cacheControl: '3600',
        upsert: false,
        contentType: 'image/webp'
      });
    
    if (error) {
      console.error('Error uploading image to storage:', error);
      return null;
    }
    
    // Get the public URL
    const { data: urlData } = supabase.storage
      .from('place_images')
      .getPublicUrl(filePath);
    
    return urlData.publicUrl;
  } catch (error) {
    console.error('Error in uploadImageToStorage:', error);
    return null;
  }
};

// Save Google Places photos to our storage
export const saveGooglePlacesPhotos = async (photoUrls: string[], placeName: string): Promise<string[]> => {
  try {
    if (!photoUrls || photoUrls.length === 0) {
      console.log('No photos to save for place:', placeName);
      return [];
    }

    console.log('Saving photos for place:', placeName, 'Photo count:', photoUrls.length);
    
    const savedUrls: string[] = [];
    
    // Process each photo URL
    for (const url of photoUrls) {
      if (!url || url.trim() === '') continue;
      
      try {
        // Download the image as a file
        const imageFile = await downloadImageAsFile(url);
        
        if (!imageFile) {
          console.warn('Failed to download image:', url);
          continue;
        }
        
        // Upload to our storage
        const storedUrl = await uploadImageToStorage(imageFile, placeName);
        
        if (storedUrl) {
          savedUrls.push(storedUrl);
        }
      } catch (photoError) {
        console.error('Error processing photo URL:', url, photoError);
      }
    }
    
    return savedUrls;
  } catch (error) {
    console.error('Error saving place photos:', error);
    return [];
  }
};

// Convert all Google Place photos to our storage
export const migrateGooglePhotosToStorage = async (place: any): Promise<any> => {
  if (!place || !place.photos || place.photos.length === 0) {
    return place;
  }
  
  // Check if photos are already in our storage (by URL pattern)
  const googlePhotosExist = place.photos.some(
    (url: string) => url.includes('maps.googleapis.com') || url.includes('maps.gstatic.com')
  );
  
  if (!googlePhotosExist) {
    // No Google photos to migrate
    return place;
  }
  
  try {
    // Extract Google photos
    const googlePhotos = place.photos.filter(
      (url: string) => url.includes('maps.googleapis.com') || url.includes('maps.gstatic.com')
    );
    
    // Keep non-Google photos
    const otherPhotos = place.photos.filter(
      (url: string) => !url.includes('maps.googleapis.com') && !url.includes('maps.gstatic.com')
    );
    
    // Update the function call to match the expected number of parameters
    const savedUrls = await saveGooglePlacesPhotos(googlePhotos, place.name);
    
    // Update the place with the combined photo URLs
    return {
      ...place,
      photos: [...otherPhotos, ...savedUrls]
    };
  } catch (error) {
    console.error('Error migrating Google photos to storage:', error);
    return place;
  }
};

// Function to copy a place to another collection
export const copyPlaceToCollection = async (
  placeId: string, 
  targetPlaylistId: string
): Promise<string | null> => {
  try {
    const { data, error } = await supabase.rpc(
      'copy_place_to_collection',
      {
        place_id: placeId,
        target_playlist_id: targetPlaylistId,
        user_id: supabase.auth.getUser().then(({ data }) => data.user?.id)
      }
    );
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error copying place to collection:', error);
    return null;
  }
};
